import { Button, PaginatedList, SearchInput } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { ManagedContentRow } from "./ManagedContentRow";
import type { useManagedContentVariant } from "./useManagedContentVariant";

export interface ManagedContentVariantContentProps {
  logic: ReturnType<typeof useManagedContentVariant>;
}

export const ManagedContentVariantContent = ({ logic }: ManagedContentVariantContentProps) => {
  const { t } = useTranslation("inbox");
  const {
    selectedIds,
    contentSearch,
    setContentSearch,
    debouncedContentSearch,
    contentQuery,
    toggleContent,
    clearSelection,
  } = logic;

  return (
    <div className="flex-1 flex flex-col overflow-hidden py-4 border-t">
      <div className="px-4 space-y-2">
        <SearchInput
          placeholder={t("Search content...")}
          value={contentSearch}
          onChange={(e) => setContentSearch(e.target.value)}
        />
        <span className="flex items-center text-xs text-muted-foreground justify-between">
          {t("{{count}} items selected", { count: selectedIds.size })}
          <Button
            variant="ghost"
            size="sm"
            className="text-xs font-light"
            onClick={clearSelection}
            disabled={!selectedIds.size}
          >
            {t("Clear")}
          </Button>
        </span>
      </div>
      {contentQuery.isLoading || contentSearch !== debouncedContentSearch ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-sm text-muted-foreground">{t("Loading content...")}</p>
        </div>
      ) : contentQuery.isError ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-sm text-destructive">{t("Error loading content")}</p>
        </div>
      ) : !contentQuery.data?.length ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-sm text-muted-foreground">{t("No content found")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 flex-1 min-h-0">
          <PaginatedList className="flex-1 min-h-0 px-4" onBottomReached={() => contentQuery.fetchNextPage()}>
            {contentQuery.data?.map((record) => (
              <ManagedContentRow
                key={record.id}
                record={record}
                selected={selectedIds.has(record.id)}
                onToggle={toggleContent}
              />
            ))}
          </PaginatedList>
        </div>
      )}
    </div>
  );
};
